import { Manipulation } from "swiper/modules";
import { OptionFilter } from "../option-filter/option-filter.ts";
import { CustomSwiper } from "../swiper/custom-swiper.js";

interface Article {
	url: string;
	image: string;
	title: string;
	date: Date;
	excerpt: string;
}

declare const $wp_ABSPATH: string;

export class SyndicatedList {
	#el: {           // cached group of <Node>|<Element>|<HTMLElement>|<NodeList>
		syndicatedList: HTMLElement;
		syndicatedListContent: HTMLElement | null;
		swiperControls: HTMLElement | null;
	};
	#tpl;                              // cached HTML5 <template>s as DocumentFragments
	#listSource: string | undefined;   // the source type of the articles - posts, hiddentruths, etc.
	#categoryId = 0;                   // {number} ID of the post category type (News Articles, Hidden Truths, etc.)
	#swiper: CustomSwiper | undefined; // CustomSwiper instance
	#tagFilterController: AbortController | undefined; // <AbortController> instance for last tag-filter fetch request made
	#itemLimit = 0;                    // {number} max number of items to fetch. -1 to fetch all (However, WP API limits to max 100 per request)

	public constructor(elSyndicatedList: HTMLElement) {
		this.#el = {
			syndicatedList: elSyndicatedList,
			syndicatedListContent: elSyndicatedList.querySelector(".syndicated-list__content"),
			swiperControls: null,
		};
		this.#listSource = elSyndicatedList.dataset['listSource'];
		this.#itemLimit = Number(elSyndicatedList.dataset['itemLimit'] ?? -1); // TODO: ensure value isn't NaN

		// create swiper instance (if applicable)
		const elSwiper = elSyndicatedList.querySelector(".swiper--syndicated-list");
		if (elSwiper instanceof HTMLElement) {
			this.#el.swiperControls = elSwiper.querySelector(".swiper-controls");

			this.#swiper = new CustomSwiper(elSwiper, {
				grabCursor: true,
				//manipulation: true,
				pagination: {
					el: ".swiper-pagination",
					dynamicBullets: true,
				},
				modules: [Manipulation],
			});
		}

		// identify templates (if provided) and initialize the option filter
		const { group: tplListGroup, item: tplListItem } = this.#getListTemplates();
		if (tplListGroup && tplListItem) {
			this.#tpl = {
				listGroup: tplListGroup,
				listItem: tplListItem,
			};

			// identify the post category
			this.#categoryId = Number(elSyndicatedList.dataset["categoryId"] ?? 0);

			const elOptionFilter = elSyndicatedList.querySelector(".option-filter");
			if (elOptionFilter && elOptionFilter instanceof HTMLInputElement) {
				new OptionFilter(elOptionFilter);
				elOptionFilter.addEventListener("optionChange", () => {
					this.#updateList([...elOptionFilter.value].map(optionValue => parseInt(optionValue, 10)));
				});
			}
		}
	}

	/**
	 * Identify the group and item templates
	 */
	#getListTemplates(): { group: DocumentFragment | null, item: DocumentFragment | null } {
		const tplGroup = this.#el.syndicatedList.querySelector(".syndicated-list__group-template");
		const tplItem = this.#el.syndicatedList.querySelector(".syndicated-list__item-template");
		const tplGroupClone = tplGroup instanceof HTMLTemplateElement ? tplGroup?.content.cloneNode(true) : null;
		const tplItemClone = tplItem instanceof HTMLTemplateElement ? tplItem?.content.cloneNode(true) : null;

		return {
			group: tplGroupClone instanceof DocumentFragment ? tplGroupClone : null,
			item: tplItemClone instanceof DocumentFragment ? tplItemClone : null,
		};
	}

	/**
	 *
	 */
	#getFilteredListItems(tagIds: number[] = []): Promise<Article[]> {
		// abort any previous fetch request and create a new AbortController to manage this request
		this.#tagFilterController?.abort();
		this.#tagFilterController = new AbortController();

		// construct the API query
		const query = new URLSearchParams({
			_embed: "wp:featuredmedia",
			_fields: "title,link,excerpt,date_gmt,_links.wp:featuredmedia,_embedded",
			per_page: String(this.#itemLimit === -1 ? 100 : this.#itemLimit), // current API allows max 100 posts - if needed more, will need to identify total # posts and loop, requesting each page
		});
		this.#categoryId && query.append("categories", String(this.#categoryId));
		tagIds.length && query.append("tags", tagIds.join(","));

		// determine which endpoint to use for the type of data
		const endpoint = new Map([
			["news", "posts"],
            ["case-studies", "posts"],
            ["white-papers", "posts"],
            ["hidden-truths", "posts"],
            ["jobpost", "posts"],
		]).get(this.#listSource ?? "");

		// fetch filtered posts list
		return fetch(
			`${$wp_ABSPATH}/wp-json/wp/v2/${endpoint}?${query.toString()}`,
			{ signal: this.#tagFilterController.signal }
		).then(response => response.json()).then((posts = []) => {
			// map response to Article[]
			return posts.map(post => {
				const postImage = post._embedded["wp:featuredmedia"][0];
				const postImageUrl =
					postImage.media_details.sizes["article-thumbnail"]?.source_url // desired thumbnail size
					?? postImage.media_details.sizes.medium?.source_url            // use 'medium' if no thumbnail generated
					?? postImage.media_details.sizes.full?.source_url
					?? postImage.media_details.source_url;                         // use original image if all else fails

				return {
					title: post.title.rendered,
					url: post.link,
					excerpt: post.excerpt.rendered,
					date: post.date_gmt + "Z", // add ISO-8601 identification as Zulu/UTC
					image: postImageUrl,
				};
			});
		});
	}

	/**
	 * Start an update of the list
	 */
	async #updateList(tagIds: number[]): Promise<void> {
		// flag list as busy/loading
		this.#el.syndicatedList.setAttribute("aria-busy", "true");

		// fetch the list items matching the provided tag IDs and populate the list
		try {
			const items = await this.#getFilteredListItems(tagIds);
			this.#populateList(items);
			// clear the busy flag
			this.#el.syndicatedList.setAttribute("aria-busy", "false");
		} catch (error) {
			// most likely fetch request was aborted - don't populate
			if (error instanceof Error && error.name !== "AbortError") {
				throw error;
			}
		}
	}

	/**
	 * Generate the HTML for the individual items and insert into the page
	 */
	#populateList(items: Article[]): void {
		// define a DocumentFragment generator for the items list
		// (this has been configured as a generator to make looping through groups/items simpler)
		const itemGetter = this.itemFragmentGenerator(items);

		let isMoreItems = true;
		const tplGroups: HTMLElement[] = [];
		while (isMoreItems) {
			const tplGroup = this.#tpl?.listGroup.cloneNode(true);
			if (!(tplGroup instanceof HTMLElement)) {
				continue;
			}

			const elItemPlaceholders = tplGroup.querySelectorAll(".syndicated-list__item--placeholder");

			// populate (or remove) each item placeholder within the group template
			let isEmptyGroup = true;
			for (const elItemPlaceholder of elItemPlaceholders) {
				const nextItem = itemGetter.next();
				const tplItem = nextItem.value;

				// if there are no more items and this group is empty, abort generating the group template
				isMoreItems = !nextItem.done;
				if (!isMoreItems && isEmptyGroup) {
					break;
				}
				isEmptyGroup = false;

				elItemPlaceholder.replaceWith(tplItem ?? "");
			}

			!isEmptyGroup && tplGroups.push(tplGroup);
		}

		// remove old list groups and add new list groups
		if (this.#swiper) {
			this.#swiper.removeAllSlides();
			this.#swiper.appendSlide(tplGroups);
			// hide swiper controls if only one slide
			if (this.#el.swiperControls) {
				this.#el.swiperControls.dataset["multipleSlides"] = (!(this.#swiper.isBeginning && this.#swiper.isEnd)).toString();
			}
		} else if (this.#el.syndicatedListContent) {
			[...this.#el.syndicatedListContent.querySelectorAll(".syndicated-list__group")].forEach(el => el.remove());
			this.#el.syndicatedListContent.append(...tplGroups);
		}
	}

	/**
	 * Sequentially generate the HTML for each item provided
	 * @yields {DocumentFragment}
	 */
	*itemFragmentGenerator(items: Article[]): IterableIterator<HTMLElement> {
		for (const item of items) {
			const tplItem = this.#tpl?.listItem.cloneNode(true);
			if (!(tplItem instanceof HTMLElement)) {
				continue;
			}

			const elLinks = tplItem.querySelectorAll(".syndicated-list__item__url");
			const elTitles = tplItem.querySelectorAll(".syndicated-list__item__title");
			const elDates = tplItem.querySelectorAll(".syndicated-list__item__date");
			const elExcerpts = tplItem.querySelectorAll(".syndicated-list__item__excerpt");
			const elImages = tplItem.querySelectorAll(".syndicated-list__item__image");

			for (const elLink of elLinks) {
				elLink.setAttribute("href", item.url);
			}

			for (const elTitle of elTitles) {
				// item.title already includes HTML escapes, which gums up the works when prepending as a DOMString
				// - parse title as HTML and prepend the unescaped text content
				elTitle.prepend((new DOMParser().parseFromString(item.title, "text/html")).documentElement.textContent ?? "");
			}

			for (const elDate of elDates) {
				const date = new Date(item.date);
				if (elDate.tagName.toLowerCase() === "time") {
					elDate.setAttribute("datetime", date.toISOString());
				}
				elDate.prepend(Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(date));
			}

			// populate 'excerpt' - treat excerpt as HTML string
			for (const elExcerpt of elExcerpts) {
				const tpl = document.createElement("template");
				tpl.innerHTML = item.excerpt;

				elExcerpt.prepend(tpl.content);
			}

			for (const elImage of elImages) {
				elImage.setAttribute("src", item.image);
				elImage.setAttribute("alt", item.title);
			}

			yield tplItem;
		}
	}

}
